import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../components/Modal';
import { backendErrorNotification } from '../../../../../components/Notification';
import SelectAsyncJSX from '../../../../../components/SelectAsync';
import HttpStatus from '../../../../../enums/httpStatus';
import {
  customersApi,
  productAPI,
  productSellerAPI,
  sellersApi,
} from '../../../../../services/requests';
import {
  ProductSeller,
  SellerProductsAndCustomers,
} from '../../../../../types/apiResponse/product';
import {
  getOptionsFromCustomers,
  getOptionsFromProducts,
  getOptionsFromSellers,
  SelectOption,
} from '../../../../../utils/getSelectOptions';

type Props = {
  closeModal: () => void;
  callback: () => void;
  selectedSeller: ProductSeller | null;
};

const CreateOrUpdateProductsSellersModal = ({ closeModal, callback, selectedSeller }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [selectedSellers, setSelectedSellers] = useState<SelectOption[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectOption[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<SelectOption[]>([]);

  const [sellerProductsData, setSellerProductsData] = useState<SellerProductsAndCustomers[]>([]);

  const getSelectedSellerLabel = (seller: ProductSeller) => {
    const sellerName = seller.seller?.Nome;

    return sellerName ? `${seller.CodigoVendedor} - ${sellerName}` : seller.CodigoVendedor;
  };

  const getSellerProductsAndCustomers = async () => {
    try {
      if (!selectedSeller) return;
      setIsLoading(true);

      productSellerAPI.query = '';
      const res = await productSellerAPI.show(selectedSeller.CodigoVendedor);

      if (res.status === HttpStatus.OK) {
        const sellerProductsDataRes: SellerProductsAndCustomers[] = res.data.data;
        setSellerProductsData(sellerProductsDataRes);
      } else {
        throw res;
      }
    } catch (err) {
      if (!productSellerAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSeller) {
      setSelectedSellers([
        {
          label: getSelectedSellerLabel(selectedSeller),
          value: selectedSeller.CodigoVendedor,
        },
      ]);

      getSellerProductsAndCustomers();
    }
  }, []);

  useEffect(() => {
    if (sellerProductsData.length > 0) {
      const productOptions = sellerProductsData.map((item) => ({
        label: `${item.productCode} - ${item.productDescription}`,
        value: item.productCode,
      }));
      setSelectedProducts(productOptions);

      // Monta um mapa para garantir que não haja clientes duplicados
      const uniqueCustomersMap = new Map<string, SelectOption>();

      sellerProductsData.forEach((product) => {
        product.customers.forEach((customer) => {
          if (!uniqueCustomersMap.has(customer.code)) {
            uniqueCustomersMap.set(customer.code, {
              label: `${customer.code} - ${customer.name}`,
              value: customer.code,
            });
          }
        });
      });

      const customerOptions = Array.from(uniqueCustomersMap.values());
      setSelectedCustomers(customerOptions);
    }
  }, [sellerProductsData]);

  const handleSaveBtn = async () => {
    try {
      setIsLoading(true);
      const sellerCodes = selectedSellers.map((item) => item.value);
      const productCodes = selectedProducts.map((item) => item.value);
      const customerCodes = selectedCustomers.map((item) => item.value);

      productSellerAPI.query = '';
      const res = await productSellerAPI.store({
        sellerCodes,
        productCodes,
        customerCodes,
      });

      if (res.status === HttpStatus.OK) {
        callback();
      } else {
        throw res;
      }
    } catch (err) {
      if (!productSellerAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={t('register')}
      showModal
      closeModal={closeModal}
      cancelButton={closeModal}
      action={() => handleSaveBtn()}
      isLoading={isLoading}
    >
      <div className="row">
        <div className="inputWithLabel">
          <div className="label">{t('sellers') as string}</div>
          {selectedSeller ? (
            <input
              className="input"
              type="text"
              disabled={isLoading}
              readOnly
              defaultValue={getSelectedSellerLabel(selectedSeller)}
            />
          ) : (
            <SelectAsyncJSX
              value={selectedSellers}
              onChange={setSelectedSellers}
              request={sellersApi}
              reqResponseToOption={getOptionsFromSellers}
              placeholder={t('sellers')}
              isMulti
              disabled={isLoading}
            />
          )}
        </div>

        <div className="inputWithLabel">
          <div className="label">{t('products') as string}</div>
          <SelectAsyncJSX
            value={selectedProducts}
            onChange={setSelectedProducts}
            request={productAPI}
            reqResponseToOption={getOptionsFromProducts}
            placeholder={t('products')}
            isMulti
            disabled={isLoading}
          />
        </div>

        <div className="inputWithLabel">
          <div className="label">{t('customers') as string}</div>
          <SelectAsyncJSX
            value={selectedCustomers}
            onChange={setSelectedCustomers}
            request={customersApi}
            reqResponseToOption={getOptionsFromCustomers}
            placeholder={t('customers')}
            isMulti
            disabled={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CreateOrUpdateProductsSellersModal;
